import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import DashboardLayout from './components/dashboard/DashboardLayout';
import Analytics from './pages/analytics/Analytics';
import LoginPage from './pages/auth/LoginPage';
import ContentApproval from './pages/content/ContentApproval';
import Dashboard from './pages/dashboard/Dashboard';
import Notifications from './pages/notifications/Notifications';
import Payments from './pages/payments/Payments';
import Pricing from './pages/pricing/Pricing';
import Settings from './pages/settings/Settings';
import Students from './pages/students/Students';
import Subscriptions from './pages/subscriptions/Subscriptions';
import Syllabus from './pages/syllabus/Syllabus';
import Teachers from './pages/teachers/Teachers';
import Users from './pages/users/Users';
import LibraryManagement from './pages/library/LibraryManagement';
import SupportInbox from './pages/support/SupportInbox';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CurriculumManager from './pages/curriculum/CurriculumManager';
import AIAnalytics from './pages/analytics/AIAnalytics';
import AppGuideManager from './pages/guide/AppGuideManager';

// Teacher Portal Pages
import TeacherSubjects from './pages/teacherPortal/TeacherSubjects';
import TeacherUploadLesson from './pages/teacherPortal/TeacherUploadLesson';
import TeacherQuizBuilder from './pages/teacherPortal/TeacherQuizBuilder';
import TeacherProgress from './pages/teacherPortal/TeacherProgress';
import TeacherMessages from './pages/teacherPortal/TeacherMessages';
import TeacherAIAssistant from './pages/teacherPortal/TeacherAIAssistant';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#64748B'
      }}>
        Loading...
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // If role check is specified and user role is loaded, verify authorization
  if (allowedRoles && userProfile && !allowedRoles.includes(userProfile.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main App Component
function AppRoutes() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="users" element={<ProtectedRoute allowedRoles={['admin']}><Users /></ProtectedRoute>} />
          <Route path="students" element={<ProtectedRoute allowedRoles={['admin']}><Students /></ProtectedRoute>} />
          <Route path="teachers" element={<ProtectedRoute allowedRoles={['admin']}><Teachers /></ProtectedRoute>} />
          <Route path="content-approval" element={<ProtectedRoute allowedRoles={['admin', 'editor']}><ContentApproval /></ProtectedRoute>} />
          <Route path="subscriptions" element={<ProtectedRoute allowedRoles={['admin']}><Subscriptions /></ProtectedRoute>} />
          <Route path="pricing" element={<ProtectedRoute allowedRoles={['admin']}><Pricing /></ProtectedRoute>} />
          <Route path="payments" element={<ProtectedRoute allowedRoles={['admin']}><Payments /></ProtectedRoute>} />
          <Route path="analytics" element={<ProtectedRoute allowedRoles={['admin']}><Analytics /></ProtectedRoute>} />
          <Route path="ai-analytics" element={<ProtectedRoute allowedRoles={['admin']}><AIAnalytics /></ProtectedRoute>} />
          <Route path="library" element={<ProtectedRoute allowedRoles={['admin', 'editor']}><LibraryManagement /></ProtectedRoute>} />
          <Route path="notifications" element={<ProtectedRoute allowedRoles={['admin']}><Notifications /></ProtectedRoute>} />
          <Route path="syllabus" element={<Navigate to="/curriculum?tab=syllabus" replace />} />
          <Route path="curriculum" element={<ProtectedRoute allowedRoles={['admin', 'editor']}><CurriculumManager /></ProtectedRoute>} />
          <Route path="app-guide" element={<ProtectedRoute allowedRoles={['admin', 'editor']}><AppGuideManager /></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute allowedRoles={['admin']}><Settings /></ProtectedRoute>} />
          <Route path="support" element={<ProtectedRoute allowedRoles={['admin']}><SupportInbox /></ProtectedRoute>} />

          {/* Shortcuts for Quiz Builder and Lesson Uploader */}
          <Route path="quiz" element={<Navigate to="/teacher/quiz" replace />} />
          <Route path="upload" element={<Navigate to="/teacher/upload" replace />} />

          {/* Teacher Portal Routes (Accessible by Admin and Teacher) */}
          <Route path="teacher/subjects" element={<ProtectedRoute allowedRoles={['admin', 'teacher']}><TeacherSubjects /></ProtectedRoute>} />
          <Route path="teacher/upload" element={<ProtectedRoute allowedRoles={['admin', 'teacher']}><TeacherUploadLesson /></ProtectedRoute>} />
          <Route path="teacher/quiz" element={<ProtectedRoute allowedRoles={['admin', 'teacher']}><TeacherQuizBuilder /></ProtectedRoute>} />
          <Route path="teacher/progress" element={<ProtectedRoute allowedRoles={['admin', 'teacher']}><TeacherProgress /></ProtectedRoute>} />
          <Route path="teacher/messages" element={<ProtectedRoute allowedRoles={['admin', 'teacher']}><TeacherMessages /></ProtectedRoute>} />
          <Route path="teacher/ai-assistant" element={<ProtectedRoute allowedRoles={['admin', 'teacher']}><TeacherAIAssistant /></ProtectedRoute>} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
